import { useEffect } from 'react';

/**
 * Smooth scroll utility for navigation
 * Handles hash links and ensures smooth scrolling to sections
 */
export const useNavigation = () => {
  useEffect(() => {
    // Handle smooth scrolling for hash links
    const handleHashLinkClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Check if it's a hash link
      if (target.tagName === 'A' && target.getAttribute('href')?.startsWith('#')) {
        event.preventDefault();
        const href = target.getAttribute('href');
        
        if (href) {
          try {
            // Get the target section
            const targetSection = document.querySelector(href);
            
            if (targetSection) {
              // Scroll smoothly to the section
              targetSection.scrollIntoView({ 
                behavior: 'smooth',
                block: 'start'
              });
              
              // Update URL hash without scrolling
              window.history.pushState(null, '', href);
            } else {
              console.warn(`Section not found: ${href}`);
            }
          } catch (error) {
            console.error('Error scrolling to section:', error);
          }
        }
      }
    };

    // Check if there's a hash in the URL when page loads
    const handleInitialHash = () => {
      const hash = window.location.hash;
      
      if (hash) {
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          try {
            const targetSection = document.querySelector(hash);
            
            if (targetSection) {
              targetSection.scrollIntoView({ 
                behavior: 'smooth',
                block: 'start'
              });
            }
          } catch (error) {
            console.error('Error scrolling to initial section:', error);
          }
        }, 100);
      }
    };

    // Add the event listener
    document.addEventListener('click', handleHashLinkClick);
    
    // Handle initial hash in URL
    handleInitialHash();

    // Clean up
    return () => {
      document.removeEventListener('click', handleHashLinkClick);
    };
  }, []);
};

/**
 * Utility to ensure all section IDs align with navigation links
 * @param sections Array of section IDs to check and ensure they exist
 */
export const ensureSectionIds = (sections: string[] = ['services', 'solutions', 'about', 'contact']) => {
  useEffect(() => {
    // Check if sections have proper IDs
    sections.forEach(sectionId => {
      const section = document.getElementById(sectionId);
      
      if (!section) {
        // Find section elements that might need IDs
        const sectionElements = document.querySelectorAll('section');
        
        sectionElements.forEach((element, index) => {
          // If this element doesn't have an ID, check if its content matches the section we're looking for
          if (!element.id) {
            // Look for headings that might indicate the section type
            const headings = element.querySelectorAll('h1, h2, h3');
            
            headings.forEach(heading => {
              if (heading.textContent?.toLowerCase().includes(sectionId)) {
                // This is likely the section we're looking for
                console.info(`Setting ID "${sectionId}" on section at index ${index}`);
                element.id = sectionId;
              }
            });
          }
        });
      }
    });
  }, [sections]);
};

export default {
  useNavigation,
  ensureSectionIds
};